import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { Input } from 'reactstrap';
import { kebabCase } from 'lodash';
import classNames from 'classnames';
import { computeHeightInPixels, computeHeightInRows } from '../heightProps';
import { conformanceSelector, enabledSelector, labelSelector, newPathSelector, placeholderSelector, valueSelector } from '../selectors/index';
import entityComposition from '../../common/classes/entityComposition';
import Label from './label';
import ConformanceTooltip from './conformanceTooltip';
import onChangeFunc from '../commonActions/onChange';
import logValueFunc from '../commonActions/logValue';
import localChangeSelector from '../selectors/localChangeSelector';
const TextField = ({
  onChange,
  path,
  logValue,
  localChangeFunc,
  operationComplete,
  operationPending,
  conformanceText: _conformanceText = null,
  label: _label = null,
  maxTextLength: _maxTextLength = null,
  onBlur: _onBlur = () => {},
  onFocus: _onFocus = () => {},
  placeholder: _placeholder = '',
  testingIdentifier: _testingIdentifier = 'default-id',
  textColor: _textColor = null,
  value: propsValue = '',
  enabled: _enabled = true,
  mdRows: _mdRows = undefined
}) => {
  const showConformance = !!_conformanceText;
  const valueRef = useRef();
  const onBlurIfChanged = async (...args) => {
    let newValue = valueRef.current.value;
    if (propsValue !== newValue) {
      if (_maxTextLength && newValue.length > _maxTextLength) {
        newValue = newValue.substring(0, _maxTextLength);
      }
      if (newValue === '') {
        newValue = null;
      }
      await _onBlur(...args);
      await operationPending();
      const log = {
        value: newValue,
        displayValue: newValue,
        path
      };
      await onChange(localChangeFunc(log));
      await logValue(log);
      await operationComplete();
    }
  };
  const className = 't-ft';
  const id = `input-${kebabCase(_label)}`;
  const testID = `${className}-${kebabCase(_testingIdentifier)}`;
  const formClasses = classNames({
    [`${className}-${id}`]: true,
    [`${testID}-input`]: true,
    'tqc-control': true,
    'tqc-text-field': true
  });

  // sometimes value can change outside of
  // the input - via behaviors for example.
  useEffect(() => {
    var _valueRef$current;
    // resets value if new propsValue comes in changed.  PropsValue only gets updated when
    // an onBlur happens.
    if ((valueRef === null || valueRef === void 0 || (_valueRef$current = valueRef.current) === null || _valueRef$current === void 0 ? void 0 : _valueRef$current.value) !== propsValue) {
      // we updated props, we should update value here.
      if (valueRef.current !== undefined) {
        valueRef.current.value = propsValue;
      }
    }
  }, [propsValue]);
  const rows = computeHeightInRows({
    mdRows: _mdRows
  });
  return /*#__PURE__*/React.createElement("div", {
    className: `${testID}-form`
  }, _label && /*#__PURE__*/React.createElement(Label, {
    value: _label,
    style: {
      height: '32px'
    },
    color: _textColor
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement(Input, {
    id: id,
    "data-testid": id,
    type: rows > 1 ? 'textarea' : 'text',
    style: rows > 1 ? {
      height: computeHeightInPixels({
        mdRows: _mdRows
      })
    } : {},
    placeholder: _placeholder,
    onBlur: onBlurIfChanged,
    onFocus: _onFocus,
    className: formClasses,
    innerRef: valueRef,
    defaultValue: propsValue,
    disabled: !_enabled,
    invalid: showConformance
  }), /*#__PURE__*/React.createElement(ConformanceTooltip, {
    "data-testid": `${id}-conformance`,
    errorText: _conformanceText,
    target: id
  })));
};
TextField.propTypes = {
  path: PropTypes.string.isRequired,
  localChangeFunc: PropTypes.func.isRequired,
  conformanceText: PropTypes.string,
  maxTextLength: PropTypes.number,
  enabled: PropTypes.bool,
  label: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  logValue: PropTypes.func.isRequired,
  operationPending: PropTypes.func.isRequired,
  operationComplete: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  testingIdentifier: PropTypes.string,
  textColor: PropTypes.shape({
    red: PropTypes.number,
    green: PropTypes.number,
    blue: PropTypes.number
  }),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mdRows: PropTypes.number
};
export { TextField as UncomposedTextField };
export default entityComposition(TextField, [], {
  conformanceText: conformanceSelector,
  enabled: enabledSelector,
  label: labelSelector,
  placeholder: placeholderSelector,
  value: valueSelector,
  path: newPathSelector,
  logValue: logValueFunc,
  onChange: onChangeFunc,
  localChangeFunc: localChangeSelector
});